// ラスターの幾何変換 (CPU、純ロジック)。ストレートアルファ RGBA。
// - 90° 回転 / 反転は画素の並べ替え (無損失)
// - 任意アフィンは逆写像 + 補間 (nearest / bilinear / bicubic)
// 補間はプリマルチプライで行い、透明画素の色が滲まないようにする。

import type { Rect, Mat3 } from '@/types/geometry'

export interface RgbaImage {
  width: number
  height: number
  data: Uint8ClampedArray
}

export type Interpolation = 'nearest' | 'bilinear' | 'bicubic'

export function flipH(img: RgbaImage): RgbaImage {
  const { width: w, height: h, data } = img
  const out = new Uint8ClampedArray(data.length)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const s = (y * w + x) * 4
      const d = (y * w + (w - 1 - x)) * 4
      out[d] = data[s]!; out[d + 1] = data[s + 1]!; out[d + 2] = data[s + 2]!; out[d + 3] = data[s + 3]!
    }
  }
  return { width: w, height: h, data: out }
}

export function flipV(img: RgbaImage): RgbaImage {
  const { width: w, height: h, data } = img
  const out = new Uint8ClampedArray(data.length)
  for (let y = 0; y < h; y++) out.set(data.subarray(y * w * 4, (y + 1) * w * 4), (h - 1 - y) * w * 4)
  return { width: w, height: h, data: out }
}

/** 時計回り 90° */
export function rotate90(img: RgbaImage): RgbaImage {
  const { width: w, height: h, data } = img
  const out = new Uint8ClampedArray(data.length)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const s = (y * w + x) * 4
      // 新しい座標: x' = h-1-y, y' = x、新しい幅 = h
      const d = (x * h + (h - 1 - y)) * 4
      out[d] = data[s]!; out[d + 1] = data[s + 1]!; out[d + 2] = data[s + 2]!; out[d + 3] = data[s + 3]!
    }
  }
  return { width: h, height: w, data: out }
}

export function rotate180(img: RgbaImage): RgbaImage {
  return flipH(flipV(img))
}

export function rotate270(img: RgbaImage): RgbaImage {
  return rotate90(rotate180(img))
}

function cubicWeight(t: number): number {
  // Catmull-Rom (a = -0.5)
  const a = -0.5
  const x = Math.abs(t)
  if (x <= 1) return (a + 2) * x * x * x - (a + 3) * x * x + 1
  if (x < 2) return a * x * x * x - 5 * a * x * x + 8 * a * x - 4 * a
  return 0
}

/** プリマルチプライされた Float32 画像 (補間用) */
function toPremul(img: RgbaImage): Float32Array {
  const n = img.width * img.height
  const out = new Float32Array(n * 4)
  for (let i = 0; i < n; i++) {
    const a = img.data[i * 4 + 3]! / 255
    out[i * 4] = img.data[i * 4]! * a
    out[i * 4 + 1] = img.data[i * 4 + 1]! * a
    out[i * 4 + 2] = img.data[i * 4 + 2]! * a
    out[i * 4 + 3] = a
  }
  return out
}

/**
 * dst の各画素を逆行列で src 座標に写して補間する。
 * @param inv dst (文書座標) → src (画像座標) の 3x3 行列
 */
export function resampleAffine(src: RgbaImage, dstRect: Rect, inv: Mat3, method: Interpolation): RgbaImage {
  const { width: sw, height: sh } = src
  const pm = method === 'nearest' ? null : toPremul(src)
  const out = new Uint8ClampedArray(dstRect.w * dstRect.h * 4)
  const sample = (x: number, y: number, c: number): number => {
    if (x < 0 || y < 0 || x >= sw || y >= sh) return 0
    return pm![(y * sw + x) * 4 + c]!
  }
  for (let dy = 0; dy < dstRect.h; dy++) {
    for (let dx = 0; dx < dstRect.w; dx++) {
      const px = dstRect.x + dx + 0.5
      const py = dstRect.y + dy + 0.5
      const pw = inv[2] * px + inv[5] * py + inv[8]
      if (pw <= 0) continue
      const sx = (inv[0] * px + inv[3] * py + inv[6]) / pw - 0.5
      const sy = (inv[1] * px + inv[4] * py + inv[7]) / pw - 0.5
      const o = (dy * dstRect.w + dx) * 4
      if (method === 'nearest') {
        const ix = Math.round(sx), iy = Math.round(sy)
        if (ix < 0 || iy < 0 || ix >= sw || iy >= sh) continue
        const s = (iy * sw + ix) * 4
        out[o] = src.data[s]!; out[o + 1] = src.data[s + 1]!; out[o + 2] = src.data[s + 2]!; out[o + 3] = src.data[s + 3]!
        continue
      }
      let r = 0, g = 0, b = 0, a = 0
      if (method === 'bilinear') {
        const x0 = Math.floor(sx), y0 = Math.floor(sy)
        const fx = sx - x0, fy = sy - y0
        for (let j = 0; j <= 1; j++) {
          for (let i = 0; i <= 1; i++) {
            const w = (i ? fx : 1 - fx) * (j ? fy : 1 - fy)
            if (w === 0) continue
            r += w * sample(x0 + i, y0 + j, 0)
            g += w * sample(x0 + i, y0 + j, 1)
            b += w * sample(x0 + i, y0 + j, 2)
            a += w * sample(x0 + i, y0 + j, 3)
          }
        }
      } else {
        const x0 = Math.floor(sx), y0 = Math.floor(sy)
        const fx = sx - x0, fy = sy - y0
        let wsum = 0
        for (let j = -1; j <= 2; j++) {
          const wy = cubicWeight(j - fy)
          for (let i = -1; i <= 2; i++) {
            const w = wy * cubicWeight(i - fx)
            if (w === 0) continue
            wsum += w
            r += w * sample(x0 + i, y0 + j, 0)
            g += w * sample(x0 + i, y0 + j, 1)
            b += w * sample(x0 + i, y0 + j, 2)
            a += w * sample(x0 + i, y0 + j, 3)
          }
        }
        if (wsum !== 0 && Math.abs(wsum - 1) > 1e-6) {
          r /= wsum; g /= wsum; b /= wsum; a /= wsum
        }
      }
      a = Math.max(0, Math.min(1, a))
      if (a <= 0) continue
      out[o] = Math.round(Math.max(0, Math.min(255, r / a)))
      out[o + 1] = Math.round(Math.max(0, Math.min(255, g / a)))
      out[o + 2] = Math.round(Math.max(0, Math.min(255, b / a)))
      out[o + 3] = Math.round(a * 255)
    }
  }
  return { width: dstRect.w, height: dstRect.h, data: out }
}

/** 拡大縮小 (画像解像度)。縮小は面積平均で前処理してから補間する (エイリアス防止) */
export function resizeImage(src: RgbaImage, w: number, h: number, method: Interpolation): RgbaImage {
  if (w === src.width && h === src.height) return src
  let base = src
  // 2 倍以上の縮小はまず整数倍のボックス縮小
  const fx = Math.floor(src.width / w), fy = Math.floor(src.height / h)
  if (method !== 'nearest' && (fx >= 2 || fy >= 2)) base = boxDownsample(src, Math.max(1, fx), Math.max(1, fy))
  const sx = base.width / w, sy = base.height / h
  const inv: Mat3 = [sx, 0, 0, 0, sy, 0, 0, 0, 1]
  return resampleAffine(base, { x: 0, y: 0, w, h }, inv, method)
}

export function boxDownsample(src: RgbaImage, fx: number, fy: number): RgbaImage {
  const w = Math.max(1, Math.floor(src.width / fx))
  const h = Math.max(1, Math.floor(src.height / fy))
  const out = new Uint8ClampedArray(w * h * 4)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let r = 0, g = 0, b = 0, a = 0, n = 0
      for (let j = 0; j < fy; j++) {
        for (let i = 0; i < fx; i++) {
          const s = ((y * fy + j) * src.width + (x * fx + i)) * 4
          const al = src.data[s + 3]!
          r += src.data[s]! * al; g += src.data[s + 1]! * al; b += src.data[s + 2]! * al; a += al; n++
        }
      }
      const o = (y * w + x) * 4
      if (a > 0) { out[o] = r / a; out[o + 1] = g / a; out[o + 2] = b / a }
      out[o + 3] = a / n
    }
  }
  return { width: w, height: h, data: out }
}

/** 行列で変換した矩形の境界 (整数) */
export function transformedBounds(rect: Rect, m: Mat3): Rect {
  const pts = [
    { x: rect.x, y: rect.y }, { x: rect.x + rect.w, y: rect.y },
    { x: rect.x, y: rect.y + rect.h }, { x: rect.x + rect.w, y: rect.y + rect.h }
  ].map(p => { const w = m[2] * p.x + m[5] * p.y + m[8] || 1; return { x: (m[0] * p.x + m[3] * p.y + m[6]) / w, y: (m[1] * p.x + m[4] * p.y + m[7]) / w } })
  const x0 = Math.floor(Math.min(...pts.map(p => p.x))), y0 = Math.floor(Math.min(...pts.map(p => p.y)))
  const x1 = Math.ceil(Math.max(...pts.map(p => p.x))), y1 = Math.ceil(Math.max(...pts.map(p => p.y)))
  return { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) }
}

export function mat3Invert(m: Mat3): Mat3 | null {
  const [a, b, c, d, e, f, g, h, i] = m
  const A = e * i - f * h, B = -(d * i - f * g), C = d * h - e * g
  const det = a * A + b * B + c * C
  if (Math.abs(det) < 1e-12) return null
  const inv = 1 / det
  return [
    A * inv, -(b * i - c * h) * inv, (b * f - c * e) * inv,
    B * inv, (a * i - c * g) * inv, -(a * f - c * d) * inv,
    C * inv, -(a * h - b * g) * inv, (a * e - b * d) * inv
  ]
}
